import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { LocalNumberPipe } from '../../pipes/localnumber.pipe';
import type { ScaleRange } from '../../providers/scales/scales.interface';

export interface ScaleLabels {
    valueLabel: string;
    referenceLabel: string;
    minRangeLabel: string;
    maxRangeLabel: string;
}

interface RangeToDisplay extends ScaleRange {
    minValue: number;
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./scale.component.ng-cli.scss'],
    selector: 'scale',
    templateUrl: 'scale.component.html',
    imports: [NgStyle, AsyncPipe, TranslatePipe, LocalNumberPipe]
})
export class ScaleComponent implements OnInit, OnChanges {
    private _observableInputsManager = new ObservableInputsManager<ScaleComponent>(this);
    @Input() ranges!: ScaleRange[];
    ranges$: Observable<ScaleRange[]> = this._observableInputsManager.observe('ranges');
    displayedRanges$!: Observable<RangeToDisplay[]>;

    @Input() value!: number;
    value$: Observable<number> = this._observableInputsManager.observe('value');
    displayedValue$!: Observable<number | null>;
    valuePosition$!: Observable<number>;

    @Input() referenceValue!: number;
    referenceValue$: Observable<number> = this._observableInputsManager.observe('referenceValue');
    displayedReferenceValue$!: Observable<number | null>;
    referenceValuePosition$!: Observable<number>;

    @Input() labels!: ScaleLabels;

    infinity: number = Infinity;

    ngOnInit() {
        this.displayedRanges$ = this.ranges$.pipe(
            filter(isNotNullish),
            map(ranges => {
                const sortedRanges = ranges.sort((range1, range2) => range1.maxValue - range2.maxValue);

                const ranks: RangeToDisplay[] = [];

                for (let i = 0; i < sortedRanges.length; i++) {
                    ranks.push({
                        ...sortedRanges[i],
                        minValue: i == 0 ? 0 : i == sortedRanges.length - 1 ? ranks[i - 1].maxValue : ranks[i - 1].maxValue + 1
                    });
                }

                return ranks;
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.displayedValue$ = this.value$.pipe(map(value => (value ? Math.round(value) : null)));

        this.displayedReferenceValue$ = this.referenceValue$.pipe(
            map(referenceValue => (referenceValue ? Math.round(referenceValue) : null))
        );

        this.valuePosition$ = combineLatest([this.displayedValue$, this.displayedRanges$]).pipe(
            map(([value, ranges]) => this.getPosition(ranges, value))
        );

        this.referenceValuePosition$ = combineLatest([
            this.displayedReferenceValue$.pipe(filter(isNotNullish)),
            this.displayedRanges$
        ]).pipe(map(([value, ranges]) => this.getPosition(ranges, value)));
    }

    private getPosition(orderedRanges: ScaleRange[], value: number | null): number {
        for (let i = 0; i < orderedRanges.length; i++) {
            const range: ScaleRange = orderedRanges[i];
            if (isNotNullish(value) && value > range.maxValue) continue;
            return i;
        }
        return 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._observableInputsManager.triggerChanges(changes);
    }
}
